import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from './AppIcon';

const ToastContext = createContext(null);

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};

let toastIdCounter = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback(({ type = 'info', title, message, duration = 5000, action }) => {
    const id = ++toastIdCounter;
    setToasts(prev => [...prev, { id, type, title, message, duration, action }]);
    if (duration > 0) {
      setTimeout(() => removeToast(id), duration);
    }
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev?.filter(t => t?.id !== id));
  }, []);

  const showError = useCallback((message, title = 'Error') => {
    return addToast({ type: 'error', title, message });
  }, [addToast]);

  const showSuccess = useCallback((message, title = 'Success') => {
    return addToast({ type: 'success', title, message });
  }, [addToast]);

  const showWarning = useCallback((message, title = 'Warning') => {
    return addToast({ type: 'warning', title, message });
  }, [addToast]);

  const showAuthError = useCallback((statusCode) => {
    if (statusCode === 401) {
      return addToast({
        type: 'warning',
        title: 'Session Expired',
        message: 'Your session has expired. Please sign in again to continue.',
        duration: 8000,
        action: { label: 'Sign In', path: '/login' }
      });
    }
    if (statusCode === 403) {
      return addToast({
        type: 'error',
        title: 'Access Denied',
        message: 'You do not have permission to perform this action.',
        duration: 6000
      });
    }
  }, [addToast]);

  return (
    <ToastContext.Provider value={{ addToast, removeToast, showError, showSuccess, showWarning, showAuthError }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

const toastStyles = {
  error: {
    container: 'bg-red-50 border-red-200 dark:bg-red-950/40 dark:border-red-800',
    icon: 'text-red-500',
    title: 'text-red-800 dark:text-red-300',
    message: 'text-red-700 dark:text-red-400',
    iconName: 'AlertCircle',
    action: 'bg-red-600 hover:bg-red-700 text-white',
  },
  warning: {
    container: 'bg-amber-50 border-amber-200 dark:bg-amber-950/40 dark:border-amber-800',
    icon: 'text-amber-500',
    title: 'text-amber-800 dark:text-amber-300',
    message: 'text-amber-700 dark:text-amber-400',
    iconName: 'AlertTriangle',
    action: 'bg-amber-600 hover:bg-amber-700 text-white',
  },
  success: {
    container: 'bg-green-50 border-green-200 dark:bg-green-950/40 dark:border-green-800',
    icon: 'text-green-500',
    title: 'text-green-800 dark:text-green-300',
    message: 'text-green-700 dark:text-green-400',
    iconName: 'CheckCircle',
    action: 'bg-green-600 hover:bg-green-700 text-white',
  },
  info: {
    container: 'bg-blue-50 border-blue-200 dark:bg-blue-950/40 dark:border-blue-800',
    icon: 'text-blue-500',
    title: 'text-blue-800 dark:text-blue-300',
    message: 'text-blue-700 dark:text-blue-400',
    iconName: 'Info',
    action: 'bg-blue-600 hover:bg-blue-700 text-white',
  },
};

const ToastItem = ({ toast, onRemove }) => {
  const navigate = useNavigate();
  const style = toastStyles?.[toast?.type] || toastStyles?.info;

  return (
    <div
      className={`flex items-start gap-3 p-4 rounded-lg border shadow-lg max-w-sm w-full animate-in slide-in-from-right-5 duration-300 ${style?.container}`}
      role="alert"
    >
      <Icon name={style?.iconName} size={18} className={`shrink-0 mt-0.5 ${style?.icon}`} />
      <div className="flex-1 min-w-0">
        {toast?.title && (
          <p className={`text-sm font-semibold leading-tight ${style?.title}`}>{toast?.title}</p>
        )}
        {toast?.message && (
          <p className={`text-xs mt-0.5 leading-snug ${style?.message}`}>{toast?.message}</p>
        )}
        {toast?.action && (
          <button
            onClick={() => { navigate(toast?.action?.path); onRemove(toast?.id); }}
            className={`mt-2 text-xs font-semibold px-3 py-1 rounded-md transition-colors ${style?.action}`}
          >
            {toast?.action?.label}
          </button>
        )}
      </div>
      <button
        onClick={() => onRemove(toast?.id)}
        className="shrink-0 p-0.5 rounded text-current opacity-50 hover:opacity-100 transition-opacity"
        aria-label="Dismiss"
      >
        <Icon name="X" size={14} />
      </button>
    </div>
  );
};

const ToastContainer = ({ toasts, onRemove }) => {
  if (toasts?.length === 0) return null;
  return (
    <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
      {toasts?.map(toast => (
        <div key={toast?.id} className="pointer-events-auto">
          <ToastItem toast={toast} onRemove={onRemove} />
        </div>
      ))}
    </div>
  );
};

export default ToastProvider;
